import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiOutlineMapPin, HiXMark } from 'react-icons/hi2'

import logoSrc from '@/assets/logo-dark.svg'
import type { Ubicacion } from '@/features/contacto/services/contacto.service'

interface Props {
  ubicaciones: Ubicacion[]
  onClose: () => void
}

export function ContactoMapModal({ ubicaciones, onClose }: Props) {
  const { t, i18n } = useTranslation('contacto')
  const [selected, setSelected] = useState(0)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const current = ubicaciones[selected]
  const mapSrc = current
    ? `https://maps.google.com/maps?q=${encodeURIComponent(current.direccion)}&output=embed&hl=${i18n.language === 'en' ? 'en' : 'es'}&z=15`
    : ''

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center bg-[rgba(0,0,0,0.45)] p-6" role="dialog" aria-modal="true" aria-label={t('map.ariaLabel')}>
      <div className="relative grid h-[min(90vh,48rem)] w-[min(90vw,72rem)] grid-cols-[clamp(14rem,28vw,22rem)_1fr] overflow-hidden rounded-md bg-white shadow-[0_1.5rem_4rem_rgba(0,0,0,0.3)] max-md:h-[90vh] max-md:grid-cols-1 max-md:grid-rows-[auto_1fr]">
        <div className="relative z-10 flex flex-col overflow-visible bg-white px-[clamp(0.75rem,1.5vw,1.75rem)] pt-[clamp(0.75rem,2.5vh,2rem)] shadow-[4px_0_20px_rgba(0,0,0,0.08)] max-md:max-h-[50vh] max-md:overflow-y-auto max-md:shadow-[0_4px_12px_rgba(0,0,0,0.08)] max-md:mr-0">
          <div className="flex-shrink-0 pb-[clamp(0.25rem,1.5vh,1rem)] text-center">
            <img src={logoSrc} alt="SynerGy" className="mx-auto mb-5 block h-auto w-full max-w-48" />
            <h2 className="m-0 mb-[clamp(0.5rem,1.5vh,1.5rem)] text-[clamp(0.875rem,1.5vw,1.25rem)] font-semibold text-[#1C3042]">
              {t('title')}
            </h2>
          </div>

          <div className="min-h-0 flex-initial overflow-y-auto bg-white shadow-[6px_0_20px_rgba(0,0,0,0.1)] -mr-16 max-md:mr-0 max-md:shadow-none">
            <ul className="m-0 flex list-none flex-col gap-0 p-0" role="list">
              {ubicaciones.map((u, i) => {
                const isActive = i === selected
                return (
                  <li key={u.nombre}>
                    <button
                      type="button"
                      className={`flex w-full cursor-pointer items-start gap-[clamp(0.35rem,0.75vw,0.75rem)] rounded-sm border-0 bg-none px-[clamp(0.5rem,1vw,0.875rem)] py-[clamp(0.4rem,1.2vh,0.875rem)] text-left transition-colors duration-150 last:border-b-0 ${
                        isActive
                          ? 'border-b border-b-transparent bg-brand-primary hover:bg-brand-primary-hover'
                          : 'border-b border-border-soft hover:bg-surface-accent-soft'
                      }`}
                      onClick={() => setSelected(i)}
                    >
                      {!isActive && (
                        <HiOutlineMapPin className="mt-[2px] h-[clamp(0.75rem,1.2vw,1.125rem)] w-[clamp(0.75rem,1.2vw,1.125rem)] shrink-0 text-brand-primary" aria-hidden />
                      )}
                      <div className="flex flex-col gap-1">
                        <span
                          className={`text-[clamp(0.7rem,1.1vw,0.9375rem)] font-bold leading-[1.3] ${isActive ? 'text-white' : 'text-text-primary'}`}
                        >
                          {u.nombre}
                          {!isActive && getCountry(u.nombre, i18n.language) && (
                            <span className={`font-normal ${isActive ? 'text-white' : 'text-text-muted'}`}>
                              {' '}
                              - {getCountry(u.nombre, i18n.language)}
                            </span>
                          )}
                        </span>
                        <span
                          className={`text-[clamp(0.6rem,0.95vw,0.8125rem)] leading-[1.4] ${isActive ? 'text-white' : 'text-text-muted'}`}
                        >
                          {u.direccion}
                        </span>
                      </div>
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>

        <div className="relative bg-[#e8eaf0]">
          <button
            type="button"
            className="absolute right-[0.875rem] top-[0.875rem] z-10 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-border-soft bg-white text-text-primary shadow-[0_2px_8px_rgba(0,0,0,0.12)] transition-colors duration-150 hover:bg-surface-accent-soft"
            onClick={onClose}
            aria-label={t('map.close')}
          >
            <HiXMark size={22} />
          </button>
          {mapSrc && (
            <iframe
              key={mapSrc}
              src={mapSrc}
              className="block h-full w-full border-0"
              title={t('map.title', { name: current.nombre })}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          )}
        </div>
      </div>
    </div>
  )
}

function getCountry(ciudad: string, lang?: string): string {
  const isEnglish = lang === 'en'
  const map: Record<string, string> = {
    Miami: isEnglish ? 'United States' : 'Estados Unidos',
    Quito: 'Ecuador',
    Guayaquil: 'Ecuador',
    Cuenca: 'Ecuador',
    Lima: isEnglish ? 'Peru' : 'Perú',
  }
  return map[ciudad] ?? ''
}
